import { entityIdFields } from '#/entity-config';

import { onlineManager } from '@tanstack/react-query';
import { Home, type LucideProps, Menu, Plus, Search, User } from 'lucide-react';
import { Suspense, lazy } from 'react';
import type { FooterLinkProps } from '~/modules/common/main-footer';
import type { UserMenuItem } from '~/modules/me/types';
import { AccountSheet } from '~/modules/navigation/account-sheet';
import { MenuSheet } from '~/modules/navigation/menu-sheet';
import { AppSearch, type SuggestionSection, type SuggestionType } from '~/modules/navigation/search';
import { useNavigationStore } from '~/store/navigation';
import { i18n } from './lib/i18n';
import { useDialoger } from './modules/common/dialoger/use-dialoger';
import Spinner from './modules/common/spinner';
import { toaster } from './modules/common/toaster';

const CreateTaskForm = lazy(() => import('~/modules/tasks/create-task-form'));

/**
 * Set entity paths so we can dynamically use them in the app
 */
export const baseEntityRoutes = {
  user: '/users/$idOrSlug',
  organization: '/$idOrSlug',
  workspace: '/$orgIdOrSlug/workspaces/$idOrSlug',
  project: '/$orgIdOrSlug/workspaces/$idOrSlug/board',
} as const;

/**
 * Type `base` for buttons in the main navigation bar, `floating` is for floating buttons
 */

export type NavItemId = (typeof navItems)[number]['id'];

export type NavItem = {
  id: NavItemId;
  icon: React.ElementType<LucideProps>;
  type: 'base' | 'floating';
  sheet?: React.ReactNode;
  action?: () => void;
  href?: string;
  mirrorOnMobile?: boolean;
};

/**
 * Declare search nav button action
 */
const startSearchAction = () => {
  if (!onlineManager.isOnline()) return toaster(i18n.t('common:action.offline.text'), 'warning');

  return useDialoger.getState().create(<AppSearch />, {
    id: 'search',
    className: 'sm:max-w-2xl p-0 border-0 mb-4',
    drawerOnMobile: false,
    hideClose: true,
  });
};

/**
 * Declare create task action
 */
const createTaskAction = () => {
  const DialogComponent = () => (
    <Suspense fallback={<Spinner className="w-10 h-10 mt-[45vh]" noDelay />}>
      <CreateTaskForm dialog />
    </Suspense>
  );

  return useDialoger.getState().create(<DialogComponent />, {
    className: 'min-w-full h-screen border-0 rounded-none flex flex-col mt-0 overflow-auto',
    drawerOnMobile: false,
    id: 'create-task',
    title: i18n.t('common:create_resource', { resource: i18n.t('app:task').toLowerCase() }),
  });
};

/**
 * Declare all of your main navigation items, visible in main navigation bar or as floating buttons on mobile
 */
export const navItems = [
  { id: 'menu', type: 'base', icon: Menu, sheet: <MenuSheet /> },
  { id: 'home', type: 'base', icon: Home, href: '/home' },
  { id: 'search', type: 'base', icon: Search, action: startSearchAction },
  { id: 'account', type: 'base', icon: User, sheet: <AccountSheet />, mirrorOnMobile: true },
  { id: 'create-task', type: 'floating', icon: Plus, action: createTaskAction },
] as const;

/**
 * Set footer links
 */
export const defaultFooterLinks: FooterLinkProps[] = [
  { id: 'about', href: '/about' },
  // { id: 'docs', href: `${config.backendUrl}/docs` },
  { id: 'legal', href: '/legal' },
];

/**
 * Set search suggestion sections
 */
export const suggestionSections: SuggestionSection[] = [
  { id: 'users', label: 'common:users', type: 'user' },
  { id: 'organizations', label: 'common:organizations', type: 'organization' },
  { id: 'workspaces', label: 'app:workspaces', type: 'workspace' },
  { id: 'projects', label: 'app:projects', type: 'project' },
];

/**
 * App-specific entity path resolver
 *
 * Since each app has its own entity structure or hierarchy, we need to resolve them dynamically in some cases.
 * For example in search suggestions and for items in the menu sheet.
 */
export const getEntityRoute = (item: UserMenuItem | SuggestionType) => {
  const {
    entity,
    slug,
    membership: { organizationId, workspaceId },
  } = item;

  // Use menu to get cached workspace/organization slug if available
  const menu = useNavigationStore.getState().menu;

  const basePath = baseEntityRoutes[entity];

  // If entity is project, set is as a query param
  const queryParams = entity === 'project' && workspaceId ? `?${entityIdFields[entity]}=${slug}` : '';

  let idOrSlug = slug;

  // If entity is project, use workspace slug if available
  if (workspaceId && entity === 'project') {
    const cachedWorkspaceSlug = menu.workspaces.find((item) => item.id === workspaceId)?.slug;
    idOrSlug = cachedWorkspaceSlug || workspaceId;
  }

  const orgIdOrSlug = menu.organizations.find((item) => item.id === organizationId)?.slug || organizationId;

  const path = `${basePath}${queryParams}`;

  return { path, params: { idOrSlug, orgIdOrSlug } };
};
